import { useBackInStockModalContext } from 'back-in-stock/src/hooks/useBackInStockModalContext/useBackInStockModalContext'
import type { BackInStockProps } from 'back-in-stock/src/types'
import { PAGE_TYPE_SHOPPING_CART } from 'cart/constants/constants'
import { useCartController } from 'cart/hooks/useCartController'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useColors } from 'pdp/src/contexts/ColorsProvider/ColorsProvider'
import { useProduct } from 'pdp/src/contexts/ProductProvider/ProductProvider'
import { useSizes } from 'pdp/src/contexts/SizesProvider/SizesProvider'
import type { Size } from 'pdp/src/types/FullProduct'
import { SELECTED_SIZE_ID_KEY } from 'product/constants/constants'
import { ProductLocations } from 'product/types/ProductLocations'
import { env } from 'utils/envManager'
import { removeFromSessionStorage } from 'utils/webStorage/sessionStorage'

interface UseSizeItemClickProps {
	isUnselectable: boolean
	withSheet: boolean
}

export function useSizeItemClick({
	isUnselectable,
	withSheet,
}: UseSizeItemClickProps): (size: Size) => void {
	const { openProductSubscription } = useBackInStockModalContext()
	const { product } = useProduct()
	const { selectedColor } = useColors()
	const { pageType } = useMasterData()
	const isCartPage = pageType === PAGE_TYPE_SHOPPING_CART
	const { add } = useCartController(isCartPage)
	const { select, isSheetOpen, openSheet, setHasErrorMessage } = useSizes()

	const handleUnselectableSize = (): void => {
		select(undefined)
		removeFromSessionStorage(SELECTED_SIZE_ID_KEY)
	}

	const handleNotAvailable = (size: Size): void => {
		const backInStockprops: BackInStockProps = {
			productId: size.relatedProductId ?? product.id,
			productName: size.relatedProductId ? size.label : product.name,
			productColorId: selectedColor.id,
			productSizeId: size.id,
			feedbackDelay: 0,
		}
		openSheet(false)
		openProductSubscription(backInStockprops)
	}

	const handleAvailable = (size: Size): void => {
		if (!withSheet || size.relatedProductId) {
			select(size)
			setHasErrorMessage(false)
		}

		if (size.relatedProductUrl) {
			const productUrl = new URL(
				size.relatedProductUrl,
				env.NEXT_PUBLIC_SITE_BASE_URL
			)

			const currentParams = new URLSearchParams(window.location.search)
			const newParams = new URLSearchParams(productUrl.search)

			currentParams.forEach((value, key) => {
				newParams.set(key, value)
			})

			productUrl.search = newParams.toString()

			window.location.assign(productUrl.pathname + productUrl.search)
		}

		if (withSheet) {
			openSheet(!isSheetOpen)

			if (!size.relatedProductId) {
				add(
					{
						productId: product.id,
						colorId: selectedColor.id,
						sizeId: size.id,
						seller: product.seller,
						look: '00',
						lastUnits: !!size.lastUnits,
					},
					ProductLocations.SizeSelector
				)
			}
		}
	}

	const handleSizeSelection = {
		unselectable: () => handleUnselectableSize(),
		notAvailable: (size: Size) => handleNotAvailable(size),
		available: (size: Size) => handleAvailable(size),
	}

	return (size: Size): void => {
		if (isUnselectable) {
			handleSizeSelection.unselectable()
			return
		}

		if (!size.available) {
			handleSizeSelection.notAvailable(size)
			return
		}

		handleSizeSelection.available(size)
	}
}
